import PrintIcon from '@mui/icons-material/Print';
import React, { useEffect, useState, useRef } from 'react';
import { Skeleton } from 'antd';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';
import { Rnd } from 'react-rnd';
import { useReactToPrint } from 'react-to-print';
import { useMobileDetect, useTabletDetection } from 'containers/chat/hooks';
import { addPrintableClass, removePrintableClass } from 'utils/methods/textMethod';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { useNavigate } from 'react-router-dom';
import { usePrintContentPDF } from 'components/hooks/usePrintContentPDF';
const ChartWrap = styled.div`
  width: 100%;
  // height: 30vh;
  // max-width: 800px;
  // margin: 0 0;       /* optional: cap on large screens */
  aspect-ratio: 4 / 3; /* height follows width */
  // min-height: 260px;        /* guarantees enough height for tiny screens */
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 8px;

  @media (max-width: 900px) {
    aspect-ratio: 3 / 4;
    min-height: 300px;
  }
  @media (max-width: 600px) {
    aspect-ratio: 1 / 1.1;
    min-height: 320px;
  }

  @media print {
    & svg text {
      opacity: 1 !important;
      visibility: visible !important;
      display: block !important;
    }
    & .recharts-tooltip-cursor,
    & .recharts-tooltip-cursor-fill,
    & .recharts-active-shape,
    & .recharts-reference-line,
    & .recharts-tooltip-wrapper {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
    }

    & svg {
      shape-rendering: crispEdges;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & .recharts-bar-rectangle {
      fill-opacity: 1 !important;
      stroke: none !important;
    }

    & rect[fill="rgba(0,0,0,0.06)"],
    & rect[fill="rgba(0,0,0,0)"] {
      display: none !important;
      visibility: hidden !important;
    }

    & text {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      opacity: 1 !important;
      visibility: visible !important;
    }
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 20;
  cursor: pointer;
  background-color: red;
  border-radius: 100%;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (pointer: coarse) {
    top: 16px;
    right: 16px;
    width: 28px;
    height: 28px;
  }

  @media print {
    display: none !important;
  }
`;

const PrintButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: #fff;
  background-color: #000;
  width: 25px;
  height: 25px;
  border-radius: 100%;
  cursor: pointer;

  @media print {
    display: none !important;
  }
`;

const ViewReportButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: #fff;
  background-color: #3871c1;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  gap: 4px;

  &:hover {
    background-color: #2a5a9f;
  }

  @media print {
    display: none !important;
  }
`;

const PrintContent = styled.div`
  position: relative;
  background-color: #f6f6f6;
  border-radius: 13px;
  border: 1px solid #000;
  padding: 20px 52px 20px 20px;
  max-width: 80vw;
  box-sizing: border-box;

  @media (pointer: coarse) {
    padding: 20px 58px 20px 20px;
  }

  @media print {
    & svg text {
      opacity: 1 !important;
      visibility: visible !important;
      display: block !important;
    }
    & text {
      opacity: 1 !important;
      visibility: visible !important;
    }
    position: relative !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 20px !important;
    background-color: white !important;
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    page-break-inside: avoid;
    break-inside: avoid;
    overflow: visible !important;

    & > div {
      page-break-inside: avoid;
      break-inside: avoid;
      overflow: visible !important;
    }

    & .recharts-tooltip-cursor,
    & .recharts-tooltip-cursor-fill,
    & .recharts-active-shape,
    & .recharts-reference-line,
    & .recharts-tooltip-wrapper,
    & rect[fill="rgba(0,0,0,0.06)"],
    & rect[fill="rgba(0,0,0,0)"] {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
    }

    & svg {
      shape-rendering: crispEdges;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & .recharts-bar-rectangle {
      fill-opacity: 1 !important;
      stroke: none !important;
    }

    @page {
      size: letter;
      margin: 15mm;
    }
  }
`;

const StandardExamEyePopUpModal = ({
  teacher,
  toggleModalOpen,
  openFlagModalOpen,
  dispatch,
  handleGetTeacherSubmissionDetails,
}) => {
  const [loading, setLoading] = useState(false);
  const [teacherData, setTeacherData] = useState(null);
  const [standardsNeedingSupport, setStandardsNeedingSupport] = useState([]);
  const [standardsExcelling, setStandardsExcelling] = useState([]);
  const [overallPerformance, setOverallPerformance] = useState(0);
  const printRef = useRef();
  const { handleDownloadPDF, isExporting } = usePrintContentPDF(printRef, {
    fileName: 'standard-exam-report.pdf',
    orientation: 'portrait',
    includeHeaderButtons: true,
  });
  const [printLoader, setPrintLoader] = useState(false);
  const isTablate = useTabletDetection();
  const isMobile = useMobileDetect() && !isTablate;
  const showAlert = useAlert();
  const navigate = useNavigate();

  useEffect(() => {
    if (teacher && teacher.teacherId) {
      fetchTeacherDetails();
    }
  }, [teacher]);


  const fetchTeacherDetails = async () => {
    if (!teacher || !teacher.teacherId) return;

    setLoading(true);
    try {
      const response = await dispatch(handleGetTeacherSubmissionDetails(teacher.teacherId));
      if (response?.success && response?.data) {
        setTeacherData(response.data.teacherData);
        setStandardsNeedingSupport(response.data.standardsNeedingSupport || []);
        setStandardsExcelling(response.data.standardsExcelling || []);
        setOverallPerformance(response.data.overallPerformance || 0);
      }
    } catch (error) {
      console.error('Error fetching teacher details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (performance) => {
    if (performance >= 80) return '#078247';
    if (performance >= 60) return '#7b13c3';
    return '#df0d14';
  };

  const getPriorityText = (performance) => {
    if (performance >= 80) return 'On Track';
    if (performance >= 60) return 'Needs Attention';
    return 'Urgent Support Needed';
  };

  const printCall = useReactToPrint({
    contentRef: printRef,
    onBeforeGetContent: async () => {
      return new Promise((resolve) => {
        setPrintLoader(true);
        setTimeout(() => resolve(), 100);
      });
    },
    onBeforePrint: async () => {
      setPrintLoader(true);
    },
    onAfterPrint: async () => {
      setPrintLoader(false);
      removePrintableClass();
    },
    onPrintError: async () => {
      setPrintLoader(false);
      removePrintableClass();
    },
  });

  const handlePrint = (e) => {
    if (isMobile || isTablate) {
      handleDownloadPDF(e);
      return;
    }
    setPrintLoader(true);
    addPrintableClass();
    setTimeout(() => {
      printCall();
    }, 100);
  };

  const handleViewReport = () => {
    if (teacher?.teacherId && teacher?.orgId) {
      navigate(`/standard-exam-report?from=teacher&orgId=${teacher.orgId}&teacherId=${teacher.teacherId}`);
    }
  };

  if (!teacher) {
    return null;
  }

  return (
    <div className='ProficyExamModalUpr'>
    <Rnd
      default={{
        x: isTablate ? 0 : -200,
        y: -200,
      }}
      bounds="window"
      enableResizing={false}
      style={{ zIndex: 1000 }}
    >
      <div className='standerdExamModal'>
        <PrintContent ref={printRef} className="to_be_print">
        <CloseButtonWrapper
          className="pdf-export-close"
          onTouchStart={toggleModalOpen}
          onClick={toggleModalOpen}
        >
          <CloseIcon style={{ width: 20, height: 20, color: 'white' }} />
        </CloseButtonWrapper>
          {/* Part1 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '1px', lineHeight: '1.2' }}
            >
              {loading ? (
                <Skeleton active paragraph={{ rows: 1 }} title={false} />
              ) : (
                <>
                  <p
                    style={{ fontSize: '14px', fontWeight: '600', color: '#252525', margin: '0px' }}
                  >
                    {teacher?.title || `${teacher?.firstName || ''} ${teacher?.lastName || ''}`}{' '}
                    {teacher?.grade ? `– ${teacher.grade} Grade` : ''} (Teacher)
                  </p>
                  <p
                    style={{ fontSize: '12px', fontWeight: '400', color: '#9CA3AF', margin: '0px' }}
                  >
                    {teacher?.school || teacherData?.schoolName?.trim() || 'N/A'}
                  </p>
                </>
              )}
            </div>
            <div style={{display:'flex', flexDirection:'row', alignItems:'center', gap:'10px'}}>
              <ViewReportButton
                className="pdf-export-hide-on-pdf"
                onTouchStart={handleViewReport}
                onClick={handleViewReport}
              >
                View Report
              </ViewReportButton>
              <PrintButtonWrapper
                className="pdf-export-print"
                onPointerDown={(e) => handlePrint(e)}
              >
                <PrintIcon sx={{ width: '18px', height: '18px' }} />
              </PrintButtonWrapper>
            </div>
          </div>

          {/* Part 2 Overall Score */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
              flexWrap: 'wrap',
              width: '50%',
            }}
          >
            <p style={{ fontSize: '14px', fontWeight: '600', margin: '0' }}>Overall Performance</p>
            {loading ? (
              <Skeleton active paragraph={{ rows: 2 }} title={false} />
            ) : (
              <>
                <div style={{
                  width: '100%',
                  height: '12px',
                  backgroundColor: '#E5E7EB',
                  borderRadius: '999px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${overallPerformance}%`,
                    height: '100%',
                    backgroundColor: '#252525',
                    borderRadius: '999px',
                    transition: 'width 0.4s ease',
                  }} />
                </div>
                <p style={{ fontSize: '12px', fontWeight: '400', margin: '0px' }}>
                  {teacherData?.averageGrade || overallPerformance}% average grade from{' '}
                  {teacherData?.totalSubmissions || 0} submissions.
                </p>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: getPriorityColor(overallPerformance),
                      padding: '2px 10px',
                      borderRadius: '38px',
                    }}
                  >
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#fff',
                        lineHeight: '1.2',
                        padding: '2px 4px',
                        margin: '0',
                      }}
                    >
                      {getPriorityText(overallPerformance)}
                    </p>
                    <InfoOutlineIcon
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        openFlagModalOpen();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openFlagModalOpen();
                      }}
                      sx={{ color: '#000', width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Part 3 Graph & Domains Needing Support */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              borderBottom: '1px solid #8a8a8a',
              marginTop: '5px',
            }}
          >
            <div style={{ flexBasis: '50%', flexShrink: 0, flexGrow: 0 }}>
              {/* Right item */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  gap: '5px',
                  width: '100%',
                  height: '100%',
                }}
              >
                <p style={{ fontSize: '14px', fontWeight: '600', margin: '0' }}>
                  Standards Needing Support
                </p>
                {loading ? (
                  <Skeleton active paragraph={{ rows: 7 }} title={false} />
                ) : standardsNeedingSupport.length > 0 ? (
                  <ChartWrap>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={standardsNeedingSupport}
                        domain={[0, 'dataMax + 1']}
                        layout="vertical"
                        margin={{ top: 24, right: 12, bottom: 8, left: 12 }}
                        barSize={100} // ← force bar width (px)
                        maxBarSize={120}
                        barCategoryGap={12}
                        barGap={4}
                      >
                        {/* vertical grid lines with horizontal background */}
                        <CartesianGrid 
                          strokeDasharray="3 3" 
                          vertical={true} 
                          horizontal={true}
                          stroke="#e5e5e5"
                        />

                        {/* Legend with a circular dot labeled "Domains" */}
                        <Legend
                          verticalAlign="top"
                          align="center"
                          iconType="circle"
                          iconSize={10}
                          wrapperStyle={{ paddingBottom: 8 }}
                        />

                        {/* Number axis across the bottom */}
                        <XAxis
                          type="number"
                          dataKey="value"
                          domain={[0, 'dataMax + 1']}
                          // domain={[0, (max) => Math.ceil(max / 5) * 5]}        // nice ticks
                          tick={{ fill: '#61677E', fontSize: 12 }}
                          tickMargin={6}
                          tickCount={5}
                          axisLine={false}
                          tickLine={false}
                        />

                        {/* Category labels on the left */}
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={90} // room for labels
                          tick={{ fill: '#61677E', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />

                        {/* Hover tooltip */}
                        <Tooltip cursor={{ fill: 'rgba(0,0,0,0.06)' }} />

                        {/* Bars: orange, rounded on the RIGHT side only; active bar brighter */}
                        <Bar
                          name="Standards"
                          dataKey="value"
                          fill="#ffa51f"
                          barSize={90}
                          radius={[0, 10, 10, 0]} // TL, TR, BR, BL
                          fillOpacity={1}
                          activeBar={{ style: { fillOpacity: 0.8 } }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartWrap>
                ) : (
                  <p style={{ fontSize: '12px', color: '#9CA3AF' }}>No standards needing support</p>
                )}
                <p>Top 3 frequently failed standards.</p>
              </div>
            </div>
            <div
              style={{
                flexBasis: '50%',
                flexShrink: 0,
                flexGrow: 0,
                width: '100%',
                height: '100%',
                alignSelf: 'flex-start',
              }}
            >
              {/* Right item */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  gap: '5px',
                  width: '100%',
                  height: '100%',
                }}
              >
                <p style={{ fontSize: '14px', fontWeight: '600', margin: '0' }}>
                  Standards Excelling
                </p>
                {loading ? (
                  <Skeleton active paragraph={{ rows: 8 }} title={false} />
                ) : standardsExcelling.length > 0 ? (
                  <ChartWrap>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={standardsExcelling}
                        layout="vertical"
                        margin={{ top: 24, right: 12, bottom: 8, left: 12 }}
                        barSize={100} // ← force bar width (px)
                        maxBarSize={120}
                        barCategoryGap={12}
                        barGap={4}
                      >
                        {/* vertical grid lines with horizontal background */}
                        <CartesianGrid 
                          strokeDasharray="3 3" 
                          vertical={true} 
                          horizontal={true}
                          stroke="#e5e5e5"
                        />

                        {/* Legend with a circular dot labeled "Domains" */}
                        <Legend
                          verticalAlign="top"
                          align="center"
                          iconType="circle"
                          iconSize={10}
                          wrapperStyle={{ paddingBottom: 8 }}
                        />

                        {/* Number axis across the bottom */}
                        <XAxis
                          type="number"
                          dataKey="value"
                          domain={[0, 'dataMax + 1']}
                          // domain={[0, (max) => Math.ceil(max / 5) * 5]}        // nice ticks
                          tick={{ fill: '#61677E', fontSize: 12 }}
                          tickMargin={6}
                          axisLine={false}
                          tickLine={false}
                        />

                        {/* Category labels on the left */}
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={90} // room for labels
                          tick={{ fill: '#61677E', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />

                        {/* Hover tooltip */}
                        <Tooltip cursor={{ fill: 'rgba(0,0,0,0.06)' }} />

                        {/* Bars: orange, rounded on the RIGHT side only; active bar brighter */}
                        <Bar
                          name="Standards"
                          dataKey="value"
                          fill="#ffa51f"
                          barSize={90}
                          radius={[0, 10, 10, 0]} // TL, TR, BR, BL
                          fillOpacity={1}
                          activeBar={{ style: { fillOpacity: 0.8 } }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartWrap>
                ) : (
                  <p style={{ fontSize: '12px', color: '#9CA3AF' }}>No standards excelling yet</p>
                )}
                <p>Top 3 frequently achieved standards.</p>
              </div>
            </div>
          </div>

          {/* Part2 temprory hidden  */}
          {/* <div style={{display:'flex', flexDirection:"column", alignItems:'flex-start'}}>
                           <p style={{fontSize:'12px', fontWeight:'600', margin:'0px'}}>Suggested Next Steps:</p>
                           <div style={{marginLeft:'20px'}}>
                           <ul>
                             <li style={{fontSize:'12px', fontWeight:'600'}}>Provide PD on teaching informational text (RI.6.3)</li>
                               <li style={{fontSize:'12px', fontWeight:'600'}}>Assign targeted small groups for RL.6.2</li>
                               <li style={{fontSize:'12px', fontWeight:'600'}}>Monitor growth on writing standards (W.6.1)</li>
                             </ul>
                           </div>
                         </div> */}
        </PrintContent>
      </div>
    </Rnd>
    </div>
  );
};

export default StandardExamEyePopUpModal;
